import React from 'react';
import { CardPreviewPane } from './components/PremiumDigitalCustomizationContent/PremiumDigitalPreviewPane';
import { PremiumDigitalCustomizationPane } from './components/PremiumDigitalCustomizationContent/PremiumDigitalCustomizationPane';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { PdpLayout } from './components/PdpLayout/PdpLayout';
import type { BasePdpImplementationProps, StationeryTemplatePDP } from './ProductDetailsPage.types';
import type { MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { usePdpColorways } from './PdpColorwayContext';
import { withSelectedColorway } from './utils';

type BasicDigitalV2PdpProps = BasePdpImplementationProps &
  Readonly<{
    template: StationeryTemplatePDP;
    onCustomizeClick: (template: MinimalRequiredTemplateDataForDraft) => void;
  }>;

/**
 * The free "Basic Digital V2" option. It renders from the paper template's
 * `themeJSON` exactly like `PremiumDigitalPdp` -- same preview, colorways and value
 * props -- but with its own preview image set (picked by the active product type) and
 * no price summary, since clicking through creates a draft and opens the digital
 * customizer at no cost.
 */
export const BasicDigitalV2Pdp = (props: BasicDigitalV2PdpProps) => {
  const { productSelector, intersectionRef, isCreatingDraft, onCustomizeClick, showCustomizeButtonInHeader, template, designName, templateCategory, eventId, isFoil } = props;

  const { options, selectedColorwayId } = usePdpColorways();

  const themeJson = template.themeJSON;

  const isMobileOrTablet = useIsMobileOrTablet();

  // Bake the selected colorway into the template so the created draft opens on
  // the chosen variant.
  const handleOnCustomizeNowClick = useEventCallback(() => {
    onCustomizeClick(withSelectedColorway(template, options, selectedColorwayId ?? ''));
  });

  return (
    <PdpLayout
      themeId={template.themeId}
      isFoil={isFoil}
      selectedColorwayId={selectedColorwayId}
      designName={designName}
      isPremium={template.premium}
      templateCategory={templateCategory}
      onCustomizeNowClick={handleOnCustomizeNowClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => (
        <CardPreviewPane
          eventId={eventId}
          templateId={template.id}
          isFavorite={template.isFavorite}
          templateCategory={templateCategory}
          cardFront={themeJson.card.front}
          cardShape={themeJson.customizations.shape}
          cardJson={themeJson}
          thumbnailUrl={template.thumbnailUrl!}
          themeId={template.themeId}
        />
      )}
      customizationPane={() => (
        // This PDP only mounts with the Basic Digital V2 gate on, so the shared value props always apply.
        <PremiumDigitalCustomizationPane
          themeId={template.themeId}
          category={templateCategory}
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          isCreatingDraft={isCreatingDraft}
          onCustomizeClick={handleOnCustomizeNowClick}
          showDigitalValueProps
        />
      )}
    />
  );
};
